<template>
  <div class="p-6 dark:text-white" v-if="tunnel">
    <div class="flex items-center justify-between mb-6">
      <h1 class="text-2xl font-semibold flex items-center dark:text-white">
        <button
          @click="goBack"
          class="mr-4 bg-blue-600 text-white px-4 py-1.5 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition duration-200 text-sm font-medium flex items-center"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="h-4 w-4 mr-1"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fill-rule="evenodd"
              d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z"
              clip-rule="evenodd"
            />
          </svg>
          Back to Tunnels
        </button>
        Tunnel Details: {{ tunnel.name }}
        <div class="relative group">
          <button
            type="button"
            class="ml-2 hover:bg-transparent bg-transparent"
            @click="startTutorial"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-circle-question-mark dark:text-gray-50 text-gray-900"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><path d="M12 17h.01"/></svg>
          </button>
          <span class="absolute top-full text-xs right-1/2 -translate-y-1/2 glass-card mb-1 px-1.5 py-0.5 text-xs text-white bg-gray-800 dark:bg-gray-700 rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap">Tutorial</span>
        </div>
      </h1>
    </div>
    
    <!-- Table Header -->
    <div class="overflow-hidden rounded-xl backdrop-blur-md bg-white/80 dark:bg-slate-900/60">
      <table class="w-full text-left">
        <thead class="text-white bg-gradient-to-r from-slate-900/95 to-slate-800/95 dark:from-slate-900/95 dark:to-slate-800/95 backdrop-blur-md border-b border-slate-700/50 dark:border-slate-700/50">
          <tr>
            <th class="py-2 px-4">Field</th>
            <th class="py-2 px-4">Original Value</th>
            <th class="py-2 px-4">New Value</th>
            <th class="py-2 px-4">Pending Value</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(field, index) in displayFields"
            :key="field.key"
            :id="'tunnel-field-' + field.key"
            :class="['border-b dark:border-gray-700', index % 2 === 0 ? 'hover:bg-gray-900/30 transition-colors' : 'hover:bg-gray-900/30 transition-colors']"
          >
            <td class="py-4 px-4">{{ field.label }}</td>
            <td class="py-4 px-4">
              <!-- Original Value -->
              <template v-if="field.key === 'site'">
                <router-link class="glass-card dark:text-white px-3.5 py-1.5" :to="{ path: `/sites/${tunnel.site}`, query: tenant.addSchemaParam() }">
                  {{ formatValue(field.key, tunnel[field.key]) }}
                </router-link>
              </template>
              <template v-else-if="field.key === 'org'">
                <router-link class="glass-card dark:text-white px-3.5 py-1.5" :to="`/organizations/${tunnel.org}`">
                  {{ formatValue(field.key, tunnel[field.key]) }}
                </router-link>
              </template>
              <template v-else>
                {{ formatValue(field.key, tunnel[field.key]) }}
              </template>
            </td>
            <td class="py-4 px-4">
              <!-- New Value -->
              <div class="flex items-center">
                <template v-if="editingField === field.key">
                  <div class="flex items-start gap-2 w-full">
                    <template v-if="field.type === 'checkbox'">
                      <GlassSelect
                        v-model="editValue"
                        :options="[{ id: true, name: 'Yes' }, { id: false, name: 'No' }]"
                        class="flex-grow"
                      />
                    </template>
                    <template v-else>
                      <div class="flex-grow">
                        <input
                          v-model="editValue"
                          :type="field.type"
                          @focus="clearFieldError(field.key)"
                          @keyup.enter="saveEdit(field.key)"
                          :class="[
                            'block w-full px-3 py-2 text-base border focus:outline-none focus:ring-2 sm:text-sm rounded-md',
                            fieldErrors[field.key]
                              ? 'ring-2 ring-red-500 border-red-500 focus:ring-red-500 focus:border-red-500'
                              : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
                          ]"
                        />
                        <FieldError v-if="fieldErrors[field.key]" :message="fieldErrors[field.key]" class="mt-1"/>
                      </div>
                    </template>
                    <div class="flex gap-2 flex-shrink-0">
                      <button
                        type="button"
                        @click.stop="saveEdit(field.key)"
                        @mousedown.prevent
                        class="btn bg-blue-600 text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        Save
                      </button>
                      <button
                        type="button"
                        @click.stop="cancelEdit(field.key)"
                        @mousedown.prevent
                        class="btn bg-gray-500 text-white hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-500"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                </template>
                <template v-else-if="handleNoEdit(field.key)">
                  <span
                    class="flex-grow cursor-pointer"
                    @click="startEdit(field.key)"
                  >
                    {{ formatValue(field.key, tunnel[field.key]) }}
                  </span>
                  <div class="relative">
                    <button
                      @click="startEdit(field.key)"
                      class="ml-2 p-1 text-gray-400 hover:text-white dark:text-white dark:hover:text-white transition-colors duration-150"
                      @mouseover="showToolTip=true, hoveritem=field.key"
                      @mouseleave="showToolTip=false"
                    >
                      <svg
                        class="h-4 w-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
                        />
                      </svg>
                    </button>
                    <span 
                      v-if="showToolTip && hoveritem === field.key"
                      class="absolute bottom-full whitespace-nowrap mb-2 transform -translate-x-1/2 rounded py-1 px-2 dark:shadow-lg dark:bg-gray-500 bg-gray-800 text-white text-center"
                    >
                      Change {{field.label}}
                    </span>
                  </div>
                </template>
              </div>
            </td>
            <td class="py-4 px-4">
              {{ getPendingValue(field.key) }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
  <div v-else>
    <p>Loading...</p>
  </div>

  <!-- Error Message -->
  <PageError v-if="error"
    :error="error"
    :errorCode="errorCode" 
  />

  <div v-else-if="!tunnel">
    <p>Loading...</p>
  </div>
</template>

<script>
import { getTunnelById, updateTunnel, getAllTunnels } from "@/services/tunnelService";
import GlassSelect from "@/components/GlassSelect.vue";
import { getSites } from "@/services/siteService";
import { useStore } from "@/store";
import { formatError } from "@/utils/errorMessages";
import PageError from "@/components/PageError.vue";
import ExplainConfigButton from "@/components/ExplainConfigButton.vue";
import { useMultiTenant } from "@/composables/useMultiTenant";
import FieldError from "@/components/FieldError.vue";
import { useTutorial } from "@/composables/tutorial";

export default {
  inheritAttrs: false,
  components: {
    PageError,
    ExplainConfigButton,
    FieldError,
    GlassSelect,
  },
  setup() {
    const tenant = useMultiTenant();
    const { start } = useTutorial();
    return { tenant, _start: start };
  },
  data() {
    return {
      showToolTip: false,
      hoveritem: null,
      tunnelDetailSteps: [
        {
          element: '#tunnel-field-name',
          action: 'display',
          message: 'The name of this tunnel.'
        },
        {
          element: '#tunnel-field-address',
          action: 'display',
          message: 'Click the edit icon to update the IP address.'
        },
        {
          element: '#tunnel-field-port',
          action: 'display',
          message: 'Click the edit icon to change the port number.'
        },
        {
          element: '#tunnel-field-primary',
          action: 'display',
          message: 'Indicates whether this is the primary tunnel.'
        },
        {
          element: '#tunnel-field-site',
          action: 'display',
          message: 'The site this tunnel is associated with.'
        },
        {
          element: '#tunnel-field-org',
          action: 'display',
          message: 'The organisation this tunnel belongs to.'
        },
        {
          element: '#tunnel-field-created_date',
          action: 'display',
          message: 'The date and time this tunnel was created.'
        },
      ],
      tunnel: null,
      sites: {},
      organizations: {},
      editingField: null,
      editValue: null,
      noEditFields: ['name', 'last_modified', 'created_date','site','org'],
      displayFields: [
        { key: "name", label: "Name", type: "text"},
        { key: "address", label: "Address", type: "text"},
        { key: "port", label: "Port", type: "number"},
        { key: "primary", label: "Primary", type: "checkbox"},
        { key: "last_modified", label: "Last Modified", type: "text"},
        { key: "created_date", label: "Created Date", type: "text"},
        { key: "site", label: "Site", type: "text"},
        { key: "org", label: "Organisation", type: "text"},
      ],
      canChangeTunnel: true,
      error: "",
      errorCode: "",
      fieldErrors: {},
      originalValue: null,
    };
  },
  async mounted() {
    try {
      const validation = this.tenant.validateSchemaForOperation();
      if (!validation.valid) {
        this.error = "Malformed request. Failed to fetch tunnel details!";
        this.errorCode = "422!";
        return;
      }
      const tunnelId = this.$route.params.id;
      const tunnels = await getAllTunnels(validation.schemaName);
      const data = tunnels.find((s) => s.id == tunnelId)
      this.tunnel = data;

      if (this.user) {
        this.canChangeTunnel = await useStore.hasPermission("Can change tunnel");
      }

      const sites = await getSites(validation.schemaName);
      this.sites = sites.reduce((acc, site) => {
        acc[site.id] = site.name;
        return acc;
      }, {});


    } catch (error) {
      console.error("Error fetching data:", error);
      const { code, message } = formatError(error);
      this.errorCode = code;
      this.error = message;
    }
  },
  computed: {
    user() {
      return useStore.getUser();
    }, 
  },
  methods: {
    async startTutorial() {
      await this._start(this.tunnelDetailSteps);
    },
    goBack() {
      this.$router.push({
        name: 'Tunnels',
        query: this.tenant.addSchemaParam()
      });
    },
    clearFieldError(fieldName){
      if (this.fieldErrors[fieldName]) {
        delete this.fieldErrors[fieldName];
        delete this.fieldErrors['non_field_errors'];
      }
    },
    startEdit(key) {
      if (this.canChangeTunnel || this.user?.is_staff) {
        if (this.handleNoEdit(key)) {
          const field = this.displayFields.find((f) => f.key === key);
          this.editingField = key;
          this.editValue = this.tunnel[key];
          this.originalValue = this.editValue;
          this.showToolTip = false;
        }
      }
    },

    cancelEdit(key) {
      this.clearFieldError(key);
      this.fieldErrors = {};
      this.editingField = null;
      this.editValue = null;
      this.tunnel[key] = this.originalValue;
    },

    goBack() {
      this.$router.push({ name: "Tunnels" });
    },

    handleNoEdit(key){
      if(this.canChangeTunnel || this.user?.is_staff){
        return !this.noEditFields.includes(key);
      }
      return false;
    },
    
    async saveEdit(key) {
      try {
        const validation = this.tenant.validateSchemaForOperation();
        if (!validation.valid) {
          this.error = "Malformed request. Failed to update tunnel!";
          this.errorCode = "422!";
          return;
        }
        const updatedData = {
          ...this.tunnel,
          [key]: this.editValue,
        };

        // Remove any undefined or null values
        Object.keys(updatedData).forEach((k) => {
          if (updatedData[k] === undefined || updatedData[k] === null) {
            delete updatedData[k];
          }
        });


        const response = await updateTunnel(
          this.tunnel.id, 
          updatedData, 
          validation.schemaName
        );

        this.tunnel = response;
        this.editingField = null;
        this.editValue = null;
      } catch (error) {
        this.fieldErrors = {}; 
        const items = this.displayFields;
        const errData = error?.response?.data;

        items.forEach(element => {
          const fieldError = errData?.[element.key];
          if (fieldError?.[0]) {
            this.fieldErrors[element.key] = fieldError[0]; 
          }
        });
      }
    },

    getPendingValue(key) {
      if (!this.tunnel?.pendingtunnel || !this.tunnel?.orginaltunnel)
        return "-";

      try {
        const pendingData = typeof this.tunnel.pendingtunnel === 'string' 
            ? JSON.parse(this.tunnel.pendingtunnel) 
            : this.tunnel.pendingtunnel;
            
        const originalData = typeof this.tunnel.orginaltunnel === 'string'
            ? JSON.parse(this.tunnel.orginaltunnel)
            : this.tunnel.orginaltunnel;

        // If the values are different, return the pending value
        if (
          JSON.stringify(pendingData[key]) !== JSON.stringify(originalData[key])
        ) {
          return this.formatValue(key, pendingData[key]);
        }
        return "-";
      } catch (error) {
        console.error("Error parsing tunnel data:", error);
        return "-";
      }
    },

    formatValue(key, value) {
      if (value === undefined || value === null) return "N/A";

      if (key === "site") {
        return this.sites[value] || "redacted";
      } else if (key === "org") {
        return this.tenant.getOrganizationName(value) || "redacted";
      } else if (key === "primary" || key === "approvedtunnel") {
        return value ? "Yes" : "No";
      } else if (key === "last_modified" || key === "created_date") {
        return new Date(value).toLocaleString();
      }

      return value.toString();
    },
  },
};
</script>
